import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import AgentLayout from '../../components/agent/AgentLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'

interface Stats {
    totalProperties: number;
    activeProperties: number;
    featuredProperties: number;
    forRent: number;
    forSale: number;
}

export default function AgentDashboard() {
    const { user, token } = useAuth()
    const [stats, setStats] = useState<Stats | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (token) {
            api.getStats(token)
                .then(data => { if (!data.message) setStats(data) })
                .catch(console.error)
                .finally(() => setLoading(false))
        } else {
            setLoading(false)
        }
    }, [token])

    return (
        <AgentLayout>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="font-black text-[#05144b] mb-1">Owner Command Center</h3>
                    <p className="text-[#05144b]/70 font-medium text-sm tracking-tight uppercase">Welcome back, <span className="font-black text-[#ffbb00]">{user?.name}</span>! Here's your property outreach metrics.</p>
                </div>
                <div>
                    <Link to="/submit-property-dashboard" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#05144b] text-[#ffbb00] border-2 border-[#ffbb00] rounded-2xl font-black text-[10px] uppercase tracking-[3px] shadow-xl hover:-translate-y-1 transition-all active:scale-95">
                        <i className="fa-solid fa-plus-circle"></i> New Listing
                    </Link>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-20 bg-white rounded-[2rem] border border-[#05144b]/5">
                    <div className="spinner-border text-[#05144b]" role="status"></div>
                    <p className="mt-4 text-[#05144b] font-black uppercase tracking-[0.2em] text-[10px]">Synchronizing Analytics...</p>
                </div>
            ) : (
                <>
                    <div className="row g-6 mb-10">
                        {[
                            { label: 'Total Inventory', value: stats?.totalProperties ?? 0, icon: 'fa-building-columns' },
                            { label: 'Active Pipeline', value: stats?.activeProperties ?? 0, icon: 'fa-chart-pie' },
                            { label: 'Elite Slots', value: stats?.featuredProperties ?? 0, icon: 'fa-crown' },
                            { label: 'Rental Leads', value: stats?.forRent ?? 0, icon: 'fa-key' },
                            { label: 'Commercial Leads', value: stats?.forSale ?? 0, icon: 'fa-handshake' },
                        ].map((s, i) => (
                            <div className="col-xl col-md-4 col-sm-6" key={i}>
                                <div className="bg-white p-6 rounded-[2rem] border border-[#05144b]/5 shadow-sm hover:shadow-xl transition-all group overflow-hidden relative">
                                    <div className="relative z-10">
                                        <p className="text-[9px] font-black text-[#05144b]/40 uppercase tracking-[0.2em] mb-4">{s.label}</p>
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-3xl font-black text-[#05144b] tracking-tighter italic">{s.value}</h3>
                                            <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#05144b] flex items-center justify-center group-hover:bg-[#05144b] group-hover:text-white transition-all duration-500">
                                                <i className={`fa-solid ${s.icon} text-lg`}></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="absolute -bottom-4 -right-4 text-blue-50/50 text-6xl group-hover:text-blue-100/50 transition-colors pointer-events-none">
                                        <i className={`fa-solid ${s.icon}`}></i>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="row g-4 mt-2">
                        {/* Quick Actions Card */}
                        <div className="col-lg-12">
                            <div className="bg-white rounded-[2.5rem] border border-[#05144b]/5 shadow-sm p-10">
                                <h5 className="text-xl font-black text-[#05144b] mb-8 uppercase italic tracking-tighter flex items-center gap-3">
                                    <div className="w-1.5 h-6 bg-[#ffbb00] rounded-full"></div>
                                    Strategic Shortcuts
                                </h5>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    <Link to="/submit-property-dashboard" className="p-8 rounded-[2rem] bg-blue-50/50 border border-[#05144b]/5 flex flex-col items-center gap-4 hover:bg-[#05144b] hover:text-white transition-all group active:scale-95">
                                        <i className="fa-solid fa-cloud-arrow-up text-3xl text-[#05144b] group-hover:text-[#ffbb00]"></i>
                                        <span className="font-black text-[10px] uppercase tracking-widest text-[#05144b] group-hover:text-white">Push Listing</span>
                                    </Link>
                                    <Link to="/my-property" className="p-8 rounded-[2rem] bg-blue-50/50 border border-[#05144b]/5 flex flex-col items-center gap-4 hover:bg-[#05144b] hover:text-white transition-all group active:scale-95">
                                        <i className="fa-solid fa-list-check text-3xl text-[#05144b] group-hover:text-[#ffbb00]"></i>
                                        <span className="font-black text-[10px] uppercase tracking-widest text-[#05144b] group-hover:text-white">Live Inventory</span>
                                    </Link>
                                    <Link to="/my-profile" className="p-8 rounded-[2rem] bg-blue-50/50 border border-[#05144b]/5 flex flex-col items-center gap-4 hover:bg-[#05144b] hover:text-white transition-all group active:scale-95">
                                        <i className="fa-solid fa-user-gear text-3xl text-[#05144b] group-hover:text-[#ffbb00]"></i>
                                        <span className="font-black text-[10px] uppercase tracking-widest text-[#05144b] group-hover:text-white">Audit Profile</span>
                                    </Link>
                                    <Link to="/change-password" className="p-8 rounded-[2rem] bg-blue-50/50 border border-[#05144b]/5 flex flex-col items-center gap-4 hover:bg-[#05144b] hover:text-white transition-all group active:scale-95">
                                        <i className="fa-solid fa-shield-halved text-3xl text-[#05144b] group-hover:text-[#ffbb00]"></i>
                                        <span className="font-black text-[10px] uppercase tracking-widest text-[#05144b] group-hover:text-white">Security Ops</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </AgentLayout>
    )
}
